import React from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '../../../components/Navbar';
import {
  UserGroupIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowLeftIcon,
  LightBulbIcon
} from '@heroicons/react/24/outline';

export default function UserAssignmentPage() {
  const { orgId } = useParams();
  const basePath = orgId ? `/${orgId}` : '';
  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <nav className="mb-8">
          <ol className="flex items-center space-x-2 text-sm">
            <li>
              <Link to={`${basePath}/help`} className="text-gray-500 hover:text-gray-700">
                Help Center
              </Link>
            </li>
            <li className="text-gray-500">/</li>
            <li>
              <Link to={`${basePath}/help/best-practices`} className="text-gray-500 hover:text-gray-700">
                Best Practices
              </Link>
            </li>
            <li className="text-gray-500">/</li>
            <li className="text-gray-900">Dynamic User Assignment</li>
          </ol>
        </nav>

        {/* Back Button */}
        <Link 
          to={`${basePath}/help/best-practices`} 
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-6"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Back to Best Practices
        </Link>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Dynamic User Assignment Best Practices
          </h1>
          <div className="flex items-center space-x-4 text-sm text-gray-600">
            <span className="inline-flex items-center px-3 py-1 rounded-full bg-yellow-100 text-yellow-800">
              Intermediate
            </span>
            <span>6 min read</span>
            <span>User Management</span>
          </div>
        </div>

        {/* Key Concept */}
        <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6 mb-8">
          <div className="flex items-start">
            <LightBulbIcon className="h-6 w-6 text-blue-600 mt-0.5" />
            <div className="ml-3">
              <h3 className="text-lg font-semibold text-blue-900 mb-2">Key Principle</h3>
              <p className="text-blue-800">
                Never hardcode user IDs. Use Queues for team assignments and dynamic lookups for specific users.
              </p>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">
            Why Queues Are Superior to Direct User Assignment
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-red-50 rounded-lg p-6">
              <h3 className="font-semibold text-red-900 mb-3 flex items-center">
                <XCircleIcon className="h-5 w-5 mr-2" />
                Problems with Direct Assignment
              </h3>
              <ul className="space-y-2 text-red-800">
                <li>• Breaks when users leave</li>
                <li>• No coverage during vacations</li>
                <li>• Manual updates for team changes</li>
                <li>• Different IDs across environments</li>
              </ul>
            </div>
            
            <div className="bg-green-50 rounded-lg p-6">
              <h3 className="font-semibold text-green-900 mb-3 flex items-center">
                <CheckCircleIcon className="h-5 w-5 mr-2" />
                Benefits of Queue Assignment
              </h3>
              <ul className="space-y-2 text-green-800">
                <li>• Multiple users can work items</li>
                <li>• Automatic coverage</li>
                <li>• Easy team management</li>
                <li>• Same name across environments</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Implementation Guide */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Implementation Guide</h2>
          
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Step 1: Create Queues</h3>
              <ol className="space-y-2 text-gray-700">
                <li>1. Go to Setup → Queues</li>
                <li>2. Create queue with descriptive name (e.g., "Support_Tier_1_Queue")</li>
                <li>3. Add team members</li>
                <li>4. Set supported objects</li>
              </ol>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Step 2: Query Queue in Flow</h3>
              <pre className="bg-gray-100 p-4 rounded text-sm overflow-x-auto">
{`Get Records: Group
Filter: DeveloperName = "Support_Tier_1_Queue"
           AND Type = "Queue"
Store: Queue_Id`}
              </pre>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Step 3: Assign to Queue</h3>
              <pre className="bg-gray-100 p-4 rounded text-sm overflow-x-auto">
{`Update Records: Case
Set: OwnerId = {!Queue_Id}`}
              </pre>
            </div>
          </div>
        </section>

        {/* Common Patterns */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Common Queue Patterns</h2>
          
          <div className="grid gap-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-2">Regional Assignment</h4>
              <p className="text-gray-700">
                Create queues like "NA_Sales_Queue", "EMEA_Sales_Queue", "APAC_Sales_Queue"
              </p>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-2">Skill-Based Routing</h4>
              <p className="text-gray-700">
                Use queues like "Technical_Support_Queue", "Billing_Support_Queue"
              </p>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-2">Priority Handling</h4>
              <p className="text-gray-700">
                Implement "High_Priority_Queue", "Standard_Queue" for SLA management
              </p>
            </div>
          </div>
        </section>

        {/* Best Practices */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Best Practices</h2>
          
          <ul className="space-y-3">
            <li className="flex items-start">
              <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
              <span>Use consistent naming convention for queues (e.g., Department_Function_Queue)</span>
            </li>
            <li className="flex items-start">
              <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
              <span>Always query by DeveloperName, not by Name (labels can be translated)</span>
            </li>
            <li className="flex items-start">
              <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
              <span>Include error handling for queue not found scenarios</span>
            </li>
            <li className="flex items-start">
              <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
              <span>Document queue purposes and membership criteria</span>
            </li>
          </ul>
        </section>

        {/* Call to Action */}
        <div className="bg-gray-100 rounded-lg p-6 text-center">
          <p className="text-gray-700 mb-4">
            Ready to implement dynamic user assignment in your flows?
          </p>
          <Link 
            to={`${basePath}/flows`} 
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            Review Your Flows
          </Link>
        </div>
      </div>
    </div>
  );
}